"use client";

import { AlertTriangle, RotateCcw } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLoadErrorRecovery } from "@/hooks/use-load-error-recovery";
import {
  captureClientEvent,
  captureClientException,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { getLoadErrorRecoveryKind } from "@/lib/load-error-recovery";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("components/ui/section-error");

type SectionErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
  /** Where this boundary sits, e.g. "admin" — used for the log scope. */
  scope: string;
  title?: string;
  description?: string;
};

/**
 * In-shell error state for a route group. Unlike the root `AppError`
 * takeover this keeps the surrounding layout (admin sidebar, queen shell,
 * mini-app nav) mounted, so a failed page is recoverable without losing
 * navigation.
 */
export function SectionError({
  error,
  reset,
  scope,
  title = "Something went wrong",
  description = "This section failed to load. Retrying usually fixes it.",
}: SectionErrorProps) {
  const recoveryKind = getLoadErrorRecoveryKind(error);
  const isRecoverable = recoveryKind !== null;

  useLoadErrorRecovery({ error, recoveryKind });

  useEffect(() => {
    const properties = {
      source: "section_error_boundary",
      scope,
      digest: error.digest,
      error_name: error.name,
      error_message: error.message?.slice(0, 500),
      has_stack: Boolean(error.stack),
      is_recoverable: isRecoverable,
      recovery_kind: recoveryKind,
    };

    logger.error("route.section_error", properties);
    // This boundary catches every in-shell route failure — admin, queen,
    // movement, weight-training and both navbar groups. Logging alone left
    // those errors out of Error Tracking entirely, so they never grouped and
    // never alerted.
    captureClientException(error, properties);
    captureClientEvent(ANALYTICS_EVENTS.SECTION_ERROR, properties);
  }, [error, isRecoverable, recoveryKind, scope]);

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <div className="rounded-full bg-warning-bg/50 p-3 text-warning">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-text-primary">
          {isRecoverable ? "Loading latest version" : title}
        </h2>
        <p className="max-w-sm text-sm text-text-secondary">
          {isRecoverable
            ? "We found an incomplete app load and are reloading a clean copy."
            : description}
        </p>
      </div>
      <Button
        type="button"
        onClick={() => {
          if (isRecoverable) {
            window.location.reload();
            return;
          }
          reset();
        }}
        variant="outline"
      >
        <RotateCcw className="h-4 w-4" />
        {isRecoverable ? "Reload now" : "Try again"}
      </Button>
      {error.digest ? (
        <p className="font-mono text-[10px] tracking-widest text-text-tertiary uppercase">
          {error.digest}
        </p>
      ) : null}
    </div>
  );
}
